/* eslint-disable complexity */
import { MutationType, TextPropertyType } from '@tencent/slide-sdk-pb-model';
import { assert } from 'ts-essentials';
import isEmpty from 'lodash/isEmpty';
import merge from 'lodash/merge';
import has from 'lodash/has';
import { BaseMutation } from '../base/base-mutation';
import { DataType } from '../../data';
import { clone } from '../../utils/protoutil';
import { Rule } from '../base/mutation-rules';
import { TextPlaceholderType } from '../../constants/text-constants';
import { shallowOmit } from '../../utils/object-utils';
import type { DeepReadonly } from 'ts-essentials';
import type { MaybeError } from '../base/base-mutation';
import type { IValidSetTextDataMutationData } from '../base/valid-mutation-data';
import type { PresentationDataModel } from '../../model/presentation-data-model';
import type {
    ICTTextParagraph,
    IEGTextRun,
    ICTTextParagraphDelta,
    IEGTextRunDelta,
    ICTTextParagraphPropertiesDelta,
    ICTTextCharacterPropertiesDelta,
    ICTTextFieldDmlADelta,
    ICTRegularTextRun,
    ICTTextFieldDmlA,
    ICTTextLineBreak,
    ITextPropertyData,
    ICTTextLineBreakDelta,
} from '@tencent/slide-sdk-pb-model';

interface IFieldData {
    fld: ICTTextFieldDmlADelta;
    index: number;
    count: number;
}

export class TextDataUtils {
    static convertParagraphEnd(
        currentParagraph: ICTTextParagraphDelta,
        curText: string,
        runProperties?: ICTTextCharacterPropertiesDelta,
        paragraphProperties?: ICTTextParagraphPropertiesDelta,
    ) {
        // 处理段落结束占位符
        if (curText === TextPlaceholderType.kParagraphBreak || curText === TextPlaceholderType.kParagraphReturn) {
            Reflect.set(currentParagraph, 'endParaRPr', runProperties);
            Reflect.set(currentParagraph, 'pPr', paragraphProperties);
            return currentParagraph;
        }
    }

    static convertTextRunProperties(curText: string, runProperties?: ICTTextCharacterPropertiesDelta) {
        // 处理软回车
        if (curText === TextPlaceholderType.kLineBreak) {
            return { rPr: runProperties } satisfies ICTTextLineBreakDelta;
        }
        // 处理通用textRun
        return { rPr: runProperties, t: curText } satisfies ICTTextFieldDmlADelta;
    }

    static convertFieldProperties(curText: string, fieldProperties?: ICTTextFieldDmlADelta): ICTTextFieldDmlADelta {
        // 处理域
        const t = curText.replace(
            new RegExp(`${TextPlaceholderType.kFieldBegin}|${TextPlaceholderType.kFieldEnd}`, 'g'),
            '',
        );
        return { rPr: {}, pPr: {}, ...fieldProperties, t };
    }

    static removeUnnecessaryData(
        runProperties?: ICTTextCharacterPropertiesDelta,
        fieldProperties?: ICTTextFieldDmlADelta,
    ) {
        // 移除 rPr 中的 isPlaceholder 属性，前端不落盘
        runProperties && Reflect.deleteProperty(runProperties, 'isPlaceholder');
        fieldProperties?.rPr && Reflect.deleteProperty(fieldProperties.rPr, 'isPlaceholder');
    }

    static fromMutationData(data: DeepReadonly<Omit<IValidSetTextDataMutationData, 'type'>>): ICTTextParagraphDelta[] {
        const { text, textProperties } = clone(data);
        const paragraphMap = new Map<number, ICTTextParagraphDelta>();
        const fieldMap = new Map<number, IFieldData>();
        // 等待处理的textRunProperty队列
        const pendingEgTextRun: IEGTextRunDelta[] = [];
        textProperties.forEach((textProperty: ITextPropertyData) => {
            const {
                index = 0,
                count = 0,
                textPropertyType,
                paragraphProperties,
                runProperties,
                fieldProperties,
            } = textProperty;
            switch (textPropertyType) {
                // 处理段落
                case TextPropertyType.Paragraph: {
                    // index 是最后一个文本的下标，不包含占位符 \r
                    paragraphMap.set(index, { egTextRun: [], endParaRPr: {}, pPr: paragraphProperties });
                    break;
                }
                // 处理文本
                case TextPropertyType.Run: {
                    // 占位符有3种情况，\r \fieldBegin \fieldEnd，这里只处理 \r
                    if (TextDataUtils.isStartFieldPlaceholder(fieldMap, index)) return;
                    if (TextDataUtils.isEndFieldPlaceholder(fieldMap, index)) {
                        const fieldData = TextDataUtils.isEndFieldPlaceholder(fieldMap, index);
                        // 处理特殊情况，中间没有文本的域，整个域只包含域开始符/域结束符，需要手动闭合文本数据。已知WPS会出现这种情况
                        if (fieldData?.count === 2) {
                            pendingEgTextRun.push(
                                merge({ fld: fieldData?.fld }, { fld: TextDataUtils.convertTextRunProperties('', {}) }),
                            );
                        }
                        return;
                    }
                    const curText = text.slice(index, index + count);
                    const curTextFieldData = TextDataUtils.isInField(fieldMap, index);
                    if (!!curTextFieldData) {
                        pendingEgTextRun.push(
                            merge(
                                { fld: shallowOmit(curTextFieldData?.fld ?? {}, 'fldType') },
                                { fld: TextDataUtils.convertTextRunProperties(curText, runProperties) },
                            ),
                        );
                    } else if (runProperties?.isPlaceholder) {
                        const currentParagraph = paragraphMap.get(index);
                        assert(currentParagraph, 'currentParagraph should exist');
                        currentParagraph.egTextRun?.push(...pendingEgTextRun.splice(0, pendingEgTextRun.length));
                        TextDataUtils.convertParagraphEnd(
                            currentParagraph,
                            curText,
                            runProperties ?? {},
                            currentParagraph.pPr ?? {},
                        );
                    } else {
                        // 处理通用textRun
                        const textRun = TextDataUtils.convertTextRunProperties(curText, runProperties);
                        const key = has(textRun, 't') ? 'r' : 'br';
                        pendingEgTextRun.push({
                            [key]: textRun,
                        });
                    }
                    break;
                }
                // 处理域
                case TextPropertyType.Field: {
                    fieldMap.set(index, {
                        fld: TextDataUtils.convertFieldProperties(text.slice(index, index + count), fieldProperties),
                        index,
                        count,
                    });
                    break;
                }
            }
            TextDataUtils.removeUnnecessaryData(runProperties, fieldProperties);
        });
        return Array.from(paragraphMap)
            .sort(([indexA], [indexB]) => indexA - indexB)
            .map(([, paragraph]) => paragraph);
    }

    static fromParagraphListData(
        paragraphList: DeepReadonly<ICTTextParagraph[]>,
        pageId: string,
        shapeId: string,
        col = 0,
        row = 0,
    ): IValidSetTextDataMutationData {
        const textParagraphs = clone(paragraphList, { deleteNull: true });
        const textProperties: ITextPropertyData[] = [];
        const setTextData: IValidSetTextDataMutationData = {
            type: MutationType.SetTextData,
            pageId,
            shapeId,
            text: '',
            textProperties,
            col,
            row,
        };
        textParagraphs.forEach((paragraph: ICTTextParagraph) => {
            const paragraphTextProperties: ITextPropertyData[] = [];
            paragraph.egTextRun?.forEach((run: IEGTextRun) => {
                if (isEmpty(run)) return;
                paragraphTextProperties.push(...TextDataUtils.fromTextRun(run, setTextData));
            });
            const baseEndParaRPrProperty = {
                index: setTextData.text.length,
                count: TextPlaceholderType.kParagraphReturn.length,
            } satisfies ITextPropertyData;
            // 设置段结束占位符
            paragraphTextProperties.push({
                ...baseEndParaRPrProperty,
                textPropertyType: TextPropertyType.Run,
                runProperties: { ...(paragraph.endParaRPr ?? {}), isPlaceholder: true },
            });
            // 设置段属性
            paragraphTextProperties.unshift({
                ...baseEndParaRPrProperty,
                textPropertyType: TextPropertyType.Paragraph,
                paragraphProperties: paragraph.pPr ?? {},
            });
            setTextData.text += TextPlaceholderType.kParagraphReturn;
            textProperties.push(...paragraphTextProperties);
        });
        return setTextData;
    }

    static fromTextRun(run: IEGTextRun, setTextData: IValidSetTextDataMutationData) {
        if (run.r) {
            return TextDataUtils.fromRegularTextRun(run.r, setTextData);
        }

        if (run.br) {
            return TextDataUtils.fromTextLineBreak(run.br, setTextData);
        }

        if (run.fld) {
            return TextDataUtils.fromTextField(run.fld, setTextData);
        }

        return [];
    }

    static fromRegularTextRun(r: ICTRegularTextRun, setTextData: IValidSetTextDataMutationData) {
        // 如果没有文本，直接返回
        if (!r.t?.length) return [];
        const textRunProperty = {
            index: setTextData.text.length,
            count: r.t?.length ?? 0,
            textPropertyType: TextPropertyType.Run,
            paragraphProperties: {},
            runProperties: r.rPr ?? {},
        } satisfies ITextPropertyData;
        Reflect.set(setTextData, 'text', (setTextData.text ?? '') + (r.t ?? ''));
        return [textRunProperty];
    }

    static fromTextLineBreak(br: ICTTextLineBreak, setTextData: IValidSetTextDataMutationData) {
        const textRunProperty = {
            index: setTextData.text.length,
            count: TextPlaceholderType.kLineBreak.length,
            textPropertyType: TextPropertyType.Run,
            paragraphProperties: {},
            runProperties: br.rPr ?? {},
        } satisfies ITextPropertyData;
        Reflect.set(setTextData, 'text', (setTextData.text ?? '') + TextPlaceholderType.kLineBreak);
        return [textRunProperty];
    }

    static getParagraphListData(
        presentation: PresentationDataModel,
        pageId: string,
        shapeId: string,
        col: number,
        row: number,
    ): IValidSetTextDataMutationData {
        const shape = presentation.store.models.get(shapeId, DataType.Shape, DataType.GraphicalObjectFrameTable);
        assert(shape, 'shape should exist');
        if (shape.type === DataType.Shape) {
            return TextDataUtils.fromParagraphListData(shape.getOwnedData().txBody?.p ?? [], pageId, shape.id);
        }
        const cell = shape.getOwnedData().graphic?.graphicData?.tbl?.tr?.[row]?.tc?.[col];
        assert(cell, 'cell should exist');
        return TextDataUtils.fromParagraphListData(cell?.txBody?.p ?? [], pageId, shapeId, col, row);
    }

    static fromTextField(fld: ICTTextFieldDmlA, setTextData: IValidSetTextDataMutationData): ITextPropertyData[] {
        const text =
            Reflect.get(TextPlaceholderType, 'kFieldBegin') +
            (fld.t ?? '') +
            Reflect.get(TextPlaceholderType, 'kFieldEnd');
        const getTextRunProperty = () => ({
            index: setTextData.text.length,
            count: 0,
            textPropertyType: TextPropertyType.Run,
            paragraphProperties: {},
            runProperties: {},
            fieldProperties: {},
        });

        const fieldProps = getTextRunProperty();
        fieldProps.textPropertyType = TextPropertyType.Field;
        fieldProps.count = text.length;
        (fieldProps.fieldProperties as ICTTextFieldDmlADelta).type = fld.type;
        (fieldProps.fieldProperties as ICTTextFieldDmlADelta).id = fld.id;
        // 前端无需理解和使用 fldType
        // fld.fldType && ((fieldProps.fieldProperties as ICTTextFieldDmlADelta).fldType = fld.fldType);

        const fieldBegin = getTextRunProperty();
        fieldBegin.count = 1;
        (fieldBegin.runProperties as ICTTextCharacterPropertiesDelta).isPlaceholder = true;
        Reflect.set(setTextData, 'text', (setTextData.text ?? '') + TextPlaceholderType.kFieldBegin);

        // 处理特殊情况，中间没有文本的域，整个域只包含域开始符/域结束符，不要生成对应的 ITextPropertyData。已知WPS文档会出现这种情况
        const fieldText = (() => {
            const count = fld.t?.length ?? 0;
            if (count === 0) return;
            const fieldText = getTextRunProperty();
            fieldText.count = fld.t?.length ?? 0;
            fieldText.runProperties = fld.rPr ?? {};
            Reflect.set(setTextData, 'text', (setTextData.text ?? '') + (fld.t ?? ''));
            return fieldText;
        })();

        const fieldEnd = getTextRunProperty();
        fieldEnd.count = 1;
        (fieldEnd.runProperties as ICTTextCharacterPropertiesDelta).isPlaceholder = true;
        Reflect.set(setTextData, 'text', (setTextData.text ?? '') + TextPlaceholderType.kFieldEnd);

        return [fieldProps, fieldBegin, fieldText, fieldEnd].filter(Boolean) as ITextPropertyData[];
    }

    static isFieldPlaceholder(fieldMap: Map<number, IFieldData>, index: number) {
        return [...fieldMap.values()].find(
            (textFieldData: { index: number; count: number }) =>
                textFieldData &&
                (textFieldData.index === index || textFieldData.index + textFieldData.count - 1 === index),
        );
    }

    static isStartFieldPlaceholder(fieldMap: Map<number, IFieldData>, index: number) {
        return [...fieldMap.values()].find(
            (textFieldData: { index: number; count: number }) => textFieldData && textFieldData.index === index,
        );
    }

    static isEndFieldPlaceholder(fieldMap: Map<number, IFieldData>, index: number) {
        return [...fieldMap.values()].find(
            (textFieldData: { index: number; count: number }) =>
                textFieldData && textFieldData.index + textFieldData.count - 1 === index,
        );
    }

    static isInField(fieldMap: Map<number, IFieldData>, index: number) {
        return [...fieldMap.values()].find(
            (textFieldData: { index: number; count: number }) =>
                textFieldData && textFieldData.index < index && textFieldData.index + textFieldData.count - 1 > index,
        );
    }
}

export class SetTextDataMutation extends BaseMutation<PresentationDataModel, IValidSetTextDataMutationData> {
    public readonly type: MutationType = MutationType.SetTextData;

    public apply(presentation: PresentationDataModel) {
        const { shapeId, col, row } = this.data;
        const shape = presentation.store.models.get(shapeId, DataType.Shape, DataType.GraphicalObjectFrameTable);
        assert(shape, 'shape should exist');

        if (shape.type === DataType.Shape) {
            shape.store.setText(TextDataUtils.fromMutationData(this.data));
            return;
        }

        if (shape.type === DataType.GraphicalObjectFrameTable) {
            const table = shape;
            table.store.setText(TextDataUtils.fromMutationData(this.data), col, row);
            return;
        }
    }

    public invert(presentation: PresentationDataModel): [IValidSetTextDataMutationData] {
        const { row, col, shapeId, pageId } = this.data;
        return [TextDataUtils.getParagraphListData(presentation, pageId, shapeId, col, row)];
    }

    public check(presentation: PresentationDataModel): MaybeError {
        const { pageId, shapeId, textProperties, col, row, text } = this.data;
        return Rule.every(
            Rule.shape.should.existInPage(presentation, shapeId, pageId),
            Rule.shape.should.hasTextBody(presentation, shapeId, col, row),
            Rule.text.textContent.is.valid(text),
            Rule.text.textProperties.should.valid(textProperties, text),
        );
    }
}
